import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input, Select, SelectItem } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { CopyableField } from '@/components/common/CopyableField';
import { Spinner } from '@/components/ui/Spinner';
import { isApiError } from '@/api/errors';
import { useTenant } from '@/contexts/TenantContext';
import { useOnboardOrganization, usePlatformCapabilities } from '@/features/platform/hooks';
import type { OnboardedOrganization } from '@/features/platform/api';
import {
  onboardOrganizationSchema, suggestSlug, type OnboardOrganizationValues,
} from '@/features/platform/schema';
import { BuildingOffice2Icon, CheckCircleIcon, ExclamationTriangleIcon, LockClosedIcon } from '@/icons';

const COUNTRIES = [
  { code: 'ZA', name: 'South Africa' }, { code: 'BW', name: 'Botswana' }, { code: 'LS', name: 'Lesotho' },
  { code: 'NA', name: 'Namibia' }, { code: 'SZ', name: 'Eswatini' }, { code: 'ZW', name: 'Zimbabwe' },
  { code: 'MZ', name: 'Mozambique' }, { code: 'GB', name: 'United Kingdom' }, { code: 'US', name: 'United States' },
];

const inputClasses = {
  label: 'text-sm font-medium text-fg',
  inputWrapper: 'border-border bg-surface hover:bg-surface focus-within:border-brand',
  input: 'text-fg placeholder:text-fg-subtle',
  description: 'text-xs text-fg-subtle',
  errorMessage: 'text-xs text-danger',
};

const FIELDS: Array<keyof OnboardOrganizationValues> = ['legalName', 'slug', 'adminEmail', 'countryCode'];

/**
 * Customer-organization onboarding (platform superadmins only). Creates the
 * organization with the caller as Owner plus a pending administrator
 * invitation. No email is sent: the invitation link is shown once for the
 * operator to deliver through a trusted channel.
 */
export function OnboardOrganizationPage() {
  const caps = usePlatformCapabilities();
  const onboard = useOnboardOrganization();
  const { switchTo, isSwitching } = useTenant();
  const [created, setCreated] = useState<OnboardedOrganization | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [slugEdited, setSlugEdited] = useState(false);

  const form = useForm<OnboardOrganizationValues>({
    resolver: zodResolver(onboardOrganizationSchema),
    defaultValues: { legalName: '', slug: '', adminEmail: '', countryCode: 'ZA' },
    mode: 'onTouched',
  });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      setCreated(await onboard.mutateAsync(values));
    } catch (e) {
      if (!isApiError(e)) { setFormError('Something went wrong. Please try again.'); return; }
      let mapped = false;
      for (const fe of e.fieldErrors) {
        const field = FIELDS.find((f) => f === fe.field);
        if (field) { form.setError(field, { message: fieldMessage(field, fe.message) }); mapped = true; }
      }
      if (e.code === 'SLUG_TAKEN') {
        form.setError('slug', { message: 'This identifier is already used by another organization. Choose a different one.' });
        setSlugEdited(true);
        return;
      }
      if (e.kind === 'forbidden') { setFormError('Only platform superadmins can onboard organizations.'); return; }
      if (e.kind === 'validation') { setFormError(mapped ? 'Please correct the highlighted fields.' : e.message); return; }
      if (e.kind === 'conflict') { setFormError(e.message); return; }
      setFormError(`${e.message} Nothing was created — it is safe to try again.`);
    }
  });

  if (caps.isLoading) {
    return <div><PageHeader title="Onboard organization" /><div className="grid place-items-center py-20"><Spinner label="Checking access…" /></div></div>;
  }
  if (!caps.canOnboard) {
    return (
      <div>
        <PageHeader title="Onboard organization" />
        <section className="flex flex-col items-center rounded-xl border border-border bg-surface px-6 py-16 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-danger/10 text-danger"><LockClosedIcon className="h-7 w-7" aria-hidden="true" /></span>
          <h2 className="mt-4 text-lg font-semibold text-fg">Platform superadmin access required</h2>
          <p className="mt-2 max-w-md text-sm text-fg-muted">
            {caps.isError
              ? 'We could not confirm your access right now. Please try again shortly.'
              : 'Only platform superadmins can onboard new customer organizations. Contact a Nifty Hosting platform owner if you need a new organization.'}
          </p>
          {caps.isError
            ? <Button className="mt-6" variant="flat" onPress={() => void caps.refetch()}>Try again</Button>
            : <Link to="/" className="mt-6 text-sm font-medium text-brand hover:underline">Back to Overview</Link>}
        </section>
      </div>
    );
  }

  if (created) {
    const inv = created.adminInvitation;
    return (
      <div>
        <PageHeader title="Organization onboarded" description="The organization is ready. You are its Owner and can switch to it now." />
        <section className="rounded-xl border border-border bg-surface p-6">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-6 w-6 shrink-0 text-success" aria-hidden="true" />
            <div>
              <h2 className="text-lg font-semibold text-fg">{created.organization.legalName}</h2>
              <p className="text-sm text-fg-muted">Account code <span className="font-mono text-fg">{created.organization.accountCode}</span> · identifier <span className="font-mono text-fg">{created.tenant.slug}</span></p>
            </div>
          </div>
          {inv && (
            <div className="mt-6 rounded-lg border border-warning/40 bg-warning/5 p-4">
              <p className="text-sm font-medium text-fg">Administrator invitation for {inv.email}</p>
              <p className="mt-1 text-xs text-fg-muted">
                No email was sent. Share this one-time link with the administrator through a trusted channel.
                It expires {new Date(inv.expiresAt).toLocaleDateString()} and will not be shown again.
              </p>
              <div className="mt-3"><CopyableField label="Invitation link" value={inv.acceptUrl} /></div>
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            <Button color="primary" isLoading={isSwitching} onPress={() => void switchTo(created.tenant.id)}>
              Switch to {created.organization.legalName}
            </Button>
            <Button variant="flat" onPress={() => { setCreated(null); setSlugEdited(false); form.reset(); }}>Onboard another</Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Onboard organization" description="Create a customer organization and invite its administrator." />
      <section className="max-w-2xl rounded-xl border border-border bg-surface p-6">
        {formError && (
          <div role="alert" className="mb-5 flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/5 p-3 text-sm text-fg">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" />{formError}
          </div>
        )}
        <form id="onboard-form" onSubmit={onSubmit} className="space-y-5" noValidate>
          <Input
            {...form.register('legalName', {
              onChange: (e) => { if (!slugEdited) form.setValue('slug', suggestSlug(e.target.value), { shouldValidate: form.formState.touchedFields.slug }); },
            })}
            label="Organization name" labelPlacement="outside" placeholder="Acme (Pty) Ltd" variant="bordered" isRequired autoFocus
            description="The customer’s registered business name."
            isInvalid={!!errors.legalName} errorMessage={errors.legalName?.message} classNames={inputClasses}
          />
          <Input
            {...form.register('slug', { onChange: () => setSlugEdited(true) })}
            label="Identifier" labelPlacement="outside" placeholder="acme" variant="bordered" isRequired
            startContent={<BuildingOffice2Icon className="h-4 w-4 text-fg-subtle" aria-hidden="true" />}
            description="Unique and permanent. Lowercase letters, numbers and hyphens (3–40 characters)."
            isInvalid={!!errors.slug} errorMessage={errors.slug?.message} classNames={inputClasses}
          />
          <Input
            {...form.register('adminEmail')}
            type="email" label="Administrator email" labelPlacement="outside" placeholder="it@acme.co.za" variant="bordered" isRequired
            autoComplete="off"
            description="Receives administrator access through an invitation link. No email is sent automatically."
            isInvalid={!!errors.adminEmail} errorMessage={errors.adminEmail?.message} classNames={inputClasses}
          />
          <Controller
            control={form.control}
            name="countryCode"
            render={({ field }) => (
              <Select
                label="Country" labelPlacement="outside" variant="bordered" isRequired
                selectedKeys={new Set([field.value])}
                onSelectionChange={(keys) => { const v = Array.from(keys)[0]; if (v) field.onChange(String(v)); }}
                isInvalid={!!errors.countryCode} errorMessage={errors.countryCode?.message}
                classNames={{ label: 'text-sm font-medium text-fg', trigger: 'border-border bg-surface' }}
              >
                {COUNTRIES.map((c) => <SelectItem key={c.code}>{c.name}</SelectItem>)}
              </Select>
            )}
          />
        </form>
        <div className="mt-6 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Link to="/" className="px-3 text-sm text-fg-muted hover:text-fg">Cancel</Link>
          <Button color="primary" type="submit" form="onboard-form" isLoading={isSubmitting} isDisabled={isSubmitting}>
            Create organization
          </Button>
        </div>
      </section>
    </div>
  );
}

function fieldMessage(field: keyof OnboardOrganizationValues, serverMessage: string): string {
  switch (field) {
    case 'slug': return serverMessage.includes('in use') ? 'This identifier is already used by another organization.' : 'Use 3–40 lowercase letters, numbers and hyphens, starting with a letter.';
    case 'adminEmail': return 'Enter a valid email address.';
    case 'legalName': return 'Enter the organization’s registered name.';
    default: return 'Check this value.';
  }
}
